/**
 * @module app.core
 */

import { autoinject, bindingMode } from 'aurelia-framework';
import { ComponentBase, SortDirection, Sorting } from 'app/shared/lib/essette-site';
import { bindable } from 'aurelia-typed-observable-plugin';
import { EssPagerCustomElement } from 'app/shared/elements/ess-pager';
import { Toaster } from 'app/shared/lib/toaster';
import { SortChangeEvent } from 'app/shared/attributes/sortable-attribute';
import { apiCall } from 'app/shared/lib/essette-api';
import { IQueryLoadOptions } from 'app/shared/lib/api/api-types';
import { confirmation } from 'app/shared/elements/ess-confirm';
import { IPopup } from 'app/shared/elements/popup-base';
import { deepClone } from 'app/shared/lib/utilities';
import { validate } from 'app/shared/elements/ess-rule';
import { ProviderService } from 'app/cd/services/provider-service';
import * as CD from 'app/cd/models/essette-api';

@autoinject()
export class SaveProviderContracts extends ComponentBase {

    ////****************** Fields and Properties ************************************////

    @bindable.object({ defaultBindingMode: bindingMode.twoWay, changeHandler: 'reload' })
    public providerGroup: CD.ProviderGroup = {};

    @bindable
    public providerContracts: ReadonlyArray<CD.ProviderContract> = [];

    protected contractHealthPlans: ReadonlyArray<CD.HealthPlan> | null = null;
    protected contractLinesOfBusiness: ReadonlyArray<CD.LineOfBusiness> | null = null;

    protected editedContract: CD.ProviderContract = {};

    protected validate = validate;

    private sorting = new Sorting();
    protected resultCount = 0;
    protected pageSize = 10;
    protected includeInactive = false;
    protected includeExpired = false;

    ////****************** Element References ***************************************////
    protected $pager!: EssPagerCustomElement;
    protected $modal!: IPopup;

    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly service: ProviderService,
        private readonly toaster: Toaster,
        ...rest: any[]) {
        super(...rest);

        // Default Sort
        this.sorting.entries = [
            {
                reference: 'healthPlan.description',
                direction: SortDirection.Ascending
            }
        ];
    }

    ////****************** Events ***************************************************////
    /** Handles the grid's sort event */
    protected async applySort(event: SortChangeEvent): Promise<void> {
        const { property, direction } = event.detail;
        const { sorting } = this;
        if(direction === null) {
            sorting.remove(property);
            if(this.sorting.entries.length == 0) {
                this.sorting.entries = [
                    {
                        reference: 'healthPlan.description',
                        direction: SortDirection.Ascending
                    }
                ];
            }
        } else {
            sorting.add({
                reference: property,
                direction: direction === 'asc' ? SortDirection.Ascending : SortDirection.Descending
            });
        }

        await this.loadProviderContractsByGroup();
        //Call stop Propagation to prevent parent table from reload and losing sort
        event.stopPropagation();
    }

    ////****************** Functions ************************************************////
    public async reload(): Promise<void> {
        await Promise.all([this.loadLookups(), this.loadProviderContractsByGroup()]);
    }

    protected saveContract(contract: CD.ProviderContract = {}): void {
        this.editedContract = deepClone(contract);
        this.$modal.open();
    }

    protected async handleFormSuccess(): Promise<void> {
        this.$modal.close();
        await this.loadProviderContractsByGroup();
    }

    ////****************** Service Calls ********************************************////
    @apiCall
    private async loadLookups(): Promise<void> {
        try {
            this.state.beginLoad();
            const [responseHealthPlan, responseLineOfBusiness] = await Promise.all([this.service.loadHealthPlans(), this.service.loadLinesOfBusiness()]);
            this.contractHealthPlans = responseHealthPlan.results;
            this.contractLinesOfBusiness = responseLineOfBusiness.results;
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }

    @apiCall
    private async loadProviderContractsByGroup(): Promise<void> {
        if(!this.providerGroup.id) return;

        const options: Partial<IQueryLoadOptions> = {
            sorting: this.sorting,
            top: this.pageSize,
            page: this.$pager?.page || 0,
            includeInactive: this.includeInactive
        };

        const includes: string[] = [
            'healthPlan',
            'lineOfBusiness'
        ];

        try {
            this.state.beginLoad();

            const response = await this.service.loadProviderContractsByGroup(this.providerGroup.id, options, includes, this.includeExpired);

            this.providerContracts = response.results;
            this.resultCount = response.count || 0;
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }

    @apiCall
    protected async activateProviderContract(contract: CD.ProviderContract): Promise<void> {
        if(!contract.id) return;

        // Show confirmation dialog only if deactivating
        if(contract.isActive) {
            const result = await confirmation.showDeactivate('cd:providerContract.$', 1, false);
            if(result.dialogResponse !== confirmation.options.OK) return;
        }

        let isSuccessful = false;
        try {
            this.state.beginSave();
            const response = await this.service.activateProviderContract(contract.id, !contract.isActive);
            isSuccessful = response.isSuccessful;
            await this.loadProviderContractsByGroup();
        }
        catch(error) {
            throw error;
        }
        finally {
            this.toaster.standard.activate('cd:providerContract.$', !contract.isActive, isSuccessful);
            this.state.endSave();
        }
    }
}
